import apiClient from "./api";

export interface ActivePatient {
  memberId: string;
  lastname: string;
  firstname: string;
  dob: string;
  diabetesProgram: boolean;
  weightProgram: boolean;
  hypertensionProgram: boolean;
  externalSystemId?: string; // CCM patient ID
  isUploading?: boolean; // Whether patient has uploaded readings
  lastUploadDate?: string; // Last reading upload date
  isUnmatched?: boolean; // True when no matching TNS record found
}

export interface ActivePatientsResponse {
  success: boolean;
  count: number;
  patients: ActivePatient[];
}

export interface IntegratedEntitiesResponse {
  success: boolean;
  entities: number[];
}

export const activePatientsService = {
  /**
   * Get list of entities with CCM integration
   */
  async getIntegratedEntities(): Promise<number[]> {
    const response = await apiClient.get<IntegratedEntitiesResponse>(
      "/utilities/active-patients/entities",
    );
    return response.data.entities;
  },

  /**
   * Get active patients for a specific entity
   */
  async getActivePatients(
    entityId: number,
    includeUploadStatus: boolean = false,
  ): Promise<ActivePatient[]> {
    const params = includeUploadStatus ? { includeUploadStatus: "true" } : {};
    const response = await apiClient.get<ActivePatientsResponse>(
      `/utilities/active-patients/${entityId}`,
      { params },
    );
    return response.data.patients;
  },

  /**
   * Download active patients as Excel file
   */
  async downloadActivePatients(
    entityId: number,
    clientName: string,
    includeUploadStatus: boolean = false,
  ): Promise<void> {
    const params: any = { clientName };
    if (includeUploadStatus) {
      params.includeUploadStatus = "true";
    }

    const response = await apiClient.get(
      `/utilities/active-patients/${entityId}/download`,
      {
        params,
        responseType: "blob",
      },
    );

    // Create download link
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    const fileName = clientName
      ? `${clientName.replace(/\s+/g, "_")}_active_patients_${new Date().toISOString().split("T")[0].replace(/-/g, "")}.xlsx`
      : `active_patients_${entityId}_${new Date().toISOString().split("T")[0].replace(/-/g, "")}.xlsx`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },

  /**
   * Download Excel from already-fetched patient data (no CCM re-fetch)
   */
  async downloadActivePatientsFromData(
    patients: ActivePatient[],
    clientName: string,
  ): Promise<void> {
    const response = await apiClient.post(
      `/utilities/active-patients/download`,
      { patients, clientName },
      { responseType: "blob" },
    );

    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement("a");
    link.href = url;
    const fileName = clientName
      ? `${clientName.replace(/\s+/g, "_")}_active_patients_${new Date().toISOString().split("T")[0].replace(/-/g, "")}.xlsx`
      : `active_patients_${new Date().toISOString().split("T")[0].replace(/-/g, "")}.xlsx`;
    link.setAttribute("download", fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
  },
};
